import { __extends } from "tslib";
import { EventEmitter } from 'eventemitter3';
import { mat3, mat4, quat, vec3, vec4 } from 'gl-matrix';
import { Landmark } from './Landmark';
import { Frustum } from '../shapes';
import { createVec3, getAngle, makePerspective } from '../utils/math';
import { requestAnimationFrame, cancelAnimationFrame } from '../utils/raf';
export var DefaultCamera = 'DefaultCamera';
export var CAMERA_TYPE;

(function (CAMERA_TYPE) {
  CAMERA_TYPE["ORBITING"] = "ORBITING";
  CAMERA_TYPE["EXPLORING"] = "EXPLORING";
  CAMERA_TYPE["TRACKING"] = "TRACKING";
})(CAMERA_TYPE || (CAMERA_TYPE = {}));

export var CAMERA_TRACKING_MODE;

(function (CAMERA_TRACKING_MODE) {
  CAMERA_TRACKING_MODE["DEFAULT"] = "DEFAULT";
  CAMERA_TRACKING_MODE["ROTATIONAL"] = "ROTATIONAL";
  CAMERA_TRACKING_MODE["TRANSLATIONAL"] = "TRANSLATIONAL";
  CAMERA_TRACKING_MODE["CINEMATIC"] = "CINEMATIC";
})(CAMERA_TRACKING_MODE || (CAMERA_TRACKING_MODE = {}));

export var CAMERA_PROJECTION_MODE;

(function (CAMERA_PROJECTION_MODE) {
  CAMERA_PROJECTION_MODE["ORTHOGRAPHIC"] = "ORTHOGRAPHIC";
  CAMERA_PROJECTION_MODE["PERSPECTIVE"] = "PERSPECTIVE";
})(CAMERA_PROJECTION_MODE || (CAMERA_PROJECTION_MODE = {}));

export var CameraEvent = {
  UPDATED: 'updated'
};
var DEG_2_RAD = Math.PI / 180;
var RAD_2_DEG = 180 / Math.PI;
/**
 * 参考「WebGL Insights - 23.Designing Cameras for WebGL Applications」，基于 Responsible Camera 思路设计
 * 保存相机参数，定义相机动作：
 * 1. dolly 沿 n 轴移动
 * 2. pan 沿 u v 轴移动
 * 3. rotate 以方位角旋转
 * 4. 移动到 Landmark，具有平滑的动画效果，其间禁止其他用户交互
 */

var Camera =
/** @class */
function (_super) {
  __extends(Camera, _super);

  function Camera() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    /**
     * 相机矩阵
     */


    _this.matrix = mat4.create();
    /**
     * u 轴
     * @see http://learnwebgl.brown37.net/07_cameras/camera_introduction.html#a-camera-definition
     */

    _this.right = vec3.fromValues(1, 0, 0);
    /**
     * v 轴 +Y is down
     */

    _this.up = vec3.fromValues(0, 1, 0);
    /**
     * n 轴 +Z is inside
     */

    _this.forward = vec3.fromValues(0, 0, 1);
    /**
     * 相机位置
     */

    _this.position = vec3.fromValues(0, 0, 1);
    /**
     * 视点位置
     */

    _this.focalPoint = vec3.fromValues(0, 0, 0);
    /**
     * 相机位置到视点向量
     * focalPoint - position
     */

    _this.distanceVector = vec3.fromValues(0, 0, 0);
    /**
     * 相机位置到视点距离
     * length(focalPoint - position)
     */

    _this.distance = 1;
    /**
     * @see https://en.wikipedia.org/wiki/Azimuth
     */

    _this.azimuth = 0;
    _this.elevation = 0;
    _this.roll = 0;
    _this.relAzimuth = 0;
    _this.relElevation = 0;
    _this.relRoll = 0;
    /**
     * 沿 n 轴移动时，保证移动速度从快到慢
     */

    _this.dollyingStep = 0;
    _this.maxDistance = Infinity;
    _this.minDistance = -Infinity;
    /**
     * zoom factor of the camera, default is 1
     * eg. https://threejs.org/docs/#api/en/cameras/OrthographicCamera.zoom
     */

    _this.zoom = 1;
    /**
     * invert the horizontal coordinate system HCS
     */

    _this.rotateWorld = false; // @inject(IDENTIFIER.InteractorService)
    // interactor: IInteractorService;

    /**
     * 投影矩阵参数
     */

    /**
     * field of view [0-360]
     * @see http://en.wikipedia.org/wiki/Angle_of_view
     */

    _this.fov = 30;
    _this.near = 0.1;
    _this.far = 1000;
    _this.aspect = 1;
    _this.projectionMatrix = mat4.create();
    _this.projectionMatrixInverse = mat4.create();
    _this.jitteredProjectionMatrix = undefined;
    _this.following = undefined;
    _this.type = CAMERA_TYPE.EXPLORING;
    _this.trackingMode = CAMERA_TRACKING_MODE.DEFAULT;
    _this.projectionMode = CAMERA_PROJECTION_MODE.PERSPECTIVE;
    /**
     * for culling use
     */

    _this.frustum = new Frustum();
    /**
     * switch between multiple landmarks
     */

    _this.landmarks = [];
    /**
     * ortho matrix for Canvas2D & SVG
     */

    _this.orthoMatrix = mat4.create();
    return _this;
  }

  Camera.prototype.isOrtho = function () {
    return this.projectionMode === CAMERA_PROJECTION_MODE.ORTHOGRAPHIC;
  };

  Camera.prototype.clone = function () {
    var camera = new Camera();
    camera.setType(this.type, undefined); // camera.interactor = this.interactor;

    return camera;
  };

  Camera.prototype.getProjectionMode = function () {
    return this.projectionMode;
  };

  Camera.prototype.getPerspective = function () {
    // account for TAA
    return this.jitteredProjectionMatrix || this.projectionMatrix;
  };

  Camera.prototype.getPerspectiveInverse = function () {
    return this.projectionMatrixInverse;
  };

  Camera.prototype.getFrustum = function () {
    return this.frustum;
  };

  Camera.prototype.getPosition = function () {
    return this.position;
  };

  Camera.prototype.getFocalPoint = function () {
    return this.focalPoint;
  };

  Camera.prototype.getNear = function () {
    return this.near;
  };

  Camera.prototype.getFar = function () {
    return this.far;
  };

  Camera.prototype.getZoom = function () {
    return this.zoom;
  };

  Camera.prototype.getOrthoMatrix = function () {
    return this.orthoMatrix;
  };

  Camera.prototype.setType = function (type, trackingMode) {
    this.type = type;

    if (this.type === CAMERA_TYPE.EXPLORING) {
      this.setWorldRotation(true);
    } else {
      this.setWorldRotation(false);
    }

    this._getAngles();

    if (this.type === CAMERA_TYPE.TRACKING && trackingMode !== undefined) {
      this.setTrackingMode(trackingMode);
    }

    return this;
  };

  Camera.prototype.setProjectionMode = function (projectionMode) {
    this.projectionMode = projectionMode;
    return this;
  };

  Camera.prototype.setTrackingMode = function (trackingMode) {
    if (this.type !== CAMERA_TYPE.TRACKING) {
      throw new Error('Impossible to set a tracking mode if the camera is not of tracking type');
    }

    this.trackingMode = trackingMode;
    return this;
  };
  /**
   * If flag is true, it reverses the azimuth and elevation angles.
   * Subsequent calls to rotate, setAzimuth, setElevation,
   * changeAzimuth or changeElevation will cause the inverted effect.
   * setRoll or changeRoll is not affected by this method.
   *
   * This inversion is useful when one wants to simulate that the world
   * is moving, instead of the camera.
   *
   * By default the camera angles are not reversed.
   * @param {Boolean} flag the boolean flag to reverse the angles.
   */


  Camera.prototype.setWorldRotation = function (flag) {
    this.rotateWorld = flag;

    this._getAngles();
  };
  /**
   * 计算 MV 矩阵，为相机矩阵的逆矩阵
   */


  Camera.prototype.getViewTransform = function () {
    return mat4.invert(mat4.create(), this.matrix);
  };

  Camera.prototype.getWorldTransform = function () {
    return this.matrix;
  };

  Camera.prototype.jitterProjectionMatrix = function (x, y) {
    var translation = mat4.fromTranslation(mat4.create(), [x, y, 0]);
    this.jitteredProjectionMatrix = mat4.multiply(mat4.create(), translation, this.projectionMatrix);
  };

  Camera.prototype.clearJitterProjectionMatrix = function () {
    this.jitteredProjectionMatrix = undefined;
  };
  /**
   * 设置相机矩阵
   */


  Camera.prototype.setMatrix = function (matrix) {
    this.matrix = matrix;

    this._update();

    return this;
  };

  Camera.prototype.setFov = function (fov) {
    this.setPerspective(this.near, this.far, fov, this.aspect);
    return this;
  };

  Camera.prototype.setAspect = function (aspect) {
    this.setPerspective(this.near, this.far, this.fov, aspect);
    return this;
  };

  Camera.prototype.setNear = function (near) {
    if (this.projectionMode === CAMERA_PROJECTION_MODE.PERSPECTIVE) {
      this.setPerspective(near, this.far, this.fov, this.aspect);
    } else {
      this.setOrthographic(this.left, this.rright, this.top, this.bottom, near, this.far);
    }

    return this;
  };

  Camera.prototype.setFar = function (far) {
    if (this.projectionMode === CAMERA_PROJECTION_MODE.PERSPECTIVE) {
      this.setPerspective(this.near, far, this.fov, this.aspect);
    } else {
      this.setOrthographic(this.left, this.rright, this.top, this.bottom, this.near, far);
    }

    return this;
  };
  /**
   * Sets an offset in a larger frustum, used in PixelPicking
   */


  Camera.prototype.setViewOffset = function (fullWidth, fullHeight, x, y, width, height) {
    this.aspect = fullWidth / fullHeight;

    if (this.view === undefined) {
      this.view = {
        enabled: true,
        fullWidth: 1,
        fullHeight: 1,
        offsetX: 0,
        offsetY: 0,
        width: 1,
        height: 1
      };
    }

    this.view.enabled = true;
    this.view.fullWidth = fullWidth;
    this.view.fullHeight = fullHeight;
    this.view.offsetX = x;
    this.view.offsetY = y;
    this.view.width = width;
    this.view.height = height;

    if (this.projectionMode === CAMERA_PROJECTION_MODE.PERSPECTIVE) {
      this.setPerspective(this.near, this.far, this.fov, this.aspect);
    } else {
      this.setOrthographic(this.left, this.rright, this.top, this.bottom, this.near, this.far);
    }

    return this;
  };

  Camera.prototype.clearViewOffset = function () {
    if (this.view !== undefined) {
      this.view.enabled = false;
    }

    if (this.projectionMode === CAMERA_PROJECTION_MODE.PERSPECTIVE) {
      this.setPerspective(this.near, this.far, this.fov, this.aspect);
    } else {
      this.setOrthographic(this.left, this.rright, this.top, this.bottom, this.near, this.far);
    }

    return this;
  };

  Camera.prototype.setZoom = function (zoom) {
    this.zoom = zoom;

    if (this.projectionMode === CAMERA_PROJECTION_MODE.ORTHOGRAPHIC) {
      this.setOrthographic(this.left, this.rright, this.top, this.bottom, this.near, this.far);
    } else if (this.projectionMode === CAMERA_PROJECTION_MODE.PERSPECTIVE) {
      this.setPerspective(this.near, this.far, this.fov, this.aspect);
    }

    return this;
  };

  Camera.prototype.setPerspective = function (near, far, fov, aspect) {
    this.projectionMode = CAMERA_PROJECTION_MODE.PERSPECTIVE;
    this.fov = fov;
    this.near = near;
    this.far = far;
    this.aspect = aspect;
    var top = this.near * Math.tan(DEG_2_RAD * 0.5 * this.fov) / this.zoom;
    var height = 2 * top;
    var width = this.aspect * height;
    var left = -0.5 * width;

    if (this.view !== undefined && this.view.enabled) {
      var fullWidth = this.view.fullWidth;
      var fullHeight = this.view.fullHeight;
      left += this.view.offsetX * width / fullWidth;
      top -= this.view.offsetY * height / fullHeight;
      width *= this.view.width / fullWidth;
      height *= this.view.height / fullHeight;
    }

    makePerspective(this.projectionMatrix, left, left + width, top, top - height, near, this.far); // flipY since the origin of OpenGL/WebGL is bottom-left compared with top-left in Canvas2D

    mat4.scale(this.projectionMatrix, this.projectionMatrix, vec3.fromValues(1, -1, 1));
    mat4.invert(this.projectionMatrixInverse, this.projectionMatrix);
    this.triggerUpdate();
    return this;
  };

  Camera.prototype.setOrthographic = function (l, r, t, b, near, far) {
    this.projectionMode = CAMERA_PROJECTION_MODE.ORTHOGRAPHIC;
    this.rright = r;
    this.left = l;
    this.top = t;
    this.bottom = b;
    this.near = near;
    this.far = far;
    var dx = (this.rright - this.left) / (2 * this.zoom);
    var dy = (this.top - this.bottom) / (2 * this.zoom);
    var cx = (this.rright + this.left) / 2;
    var cy = (this.top + this.bottom) / 2;
    var left = cx - dx;
    var right = cx + dx;
    var top = cy + dy;
    var bottom = cy - dy;

    if (this.view !== undefined && this.view.enabled) {
      var scaleW = (this.rright - this.left) / this.view.fullWidth / this.zoom;
      var scaleH = (this.top - this.bottom) / this.view.fullHeight / this.zoom;
      left += scaleW * this.view.offsetX;
      right = left + scaleW * this.view.width;
      top -= scaleH * this.view.offsetY;
      bottom = top - scaleH * this.view.height;
    }

    mat4.ortho(this.projectionMatrix, left, right, bottom, top, near, far); // flipY since the origin of OpenGL/WebGL is bottom-left compared with top-left in Canvas2D

    mat4.scale(this.projectionMatrix, this.projectionMatrix, vec3.fromValues(1, -1, 1));
    mat4.invert(this.projectionMatrixInverse, this.projectionMatrix);

    this._getOrthoMatrix();

    this.triggerUpdate();
    return this;
  };
  /**
   * 设置相机位置
   */


  Camera.prototype.setPosition = function (x, y, z) {
    if (y === void 0) {
      y = 0;
    }

    if (z === void 0) {
      z = 0;
    }

    this._setPosition(x, y, z);

    this.setFocalPoint(this.focalPoint);
    return this;
  };
  /**
   * 设置视点位置
   */


  Camera.prototype.setFocalPoint = function (x, y, z) {
    var up = vec3.fromValues(0, 1, 0);
    this.focalPoint = createVec3(x, y, z);

    if (this.trackingMode === CAMERA_TRACKING_MODE.CINEMATIC) {
      var d = vec3.subtract(vec3.create(), this.focalPoint, this.position);
      x = d[0];
      y = d[1];
      z = d[2];
      var r = vec3.length(d);
      var el = Math.asin(y / r) * RAD_2_DEG;
      var az = 90 + Math.atan2(z, x) * RAD_2_DEG;
      var m = mat4.create();
      mat4.rotateY(m, m, az * DEG_2_RAD);
      mat4.rotateX(m, m, el * DEG_2_RAD);
      up = vec3.transformMat4(vec3.create(), [0, 1, 0], m);
    }

    mat4.invert(this.matrix, mat4.lookAt(mat4.create(), this.position, this.focalPoint, up));

    this._getAxes();

    this._getDistance();

    this._getAngles();

    return this;
  };
  /**
   * 固定当前视点，按指定距离放置相机
   */


  Camera.prototype.setDistance = function (d) {
    if (this.distance === d || d < 0) {
      return;
    }

    this.distance = d;

    if (this.distance < 0.0002) {
      this.distance = 0.0002;
    }

    this.dollyingStep = this.distance / 100;
    var pos = vec3.create();
    d = this.distance;
    var n = this.forward;
    var f = this.focalPoint;
    pos[0] = d * n[0] + f[0];
    pos[1] = d * n[1] + f[1];
    pos[2] = d * n[2] + f[2];

    this._setPosition(pos);

    return this;
  };

  Camera.prototype.setMaxDistance = function (d) {
    this.maxDistance = d;
    return this;
  };

  Camera.prototype.setMinDistance = function (d) {
    this.minDistance = d;
    return this;
  };
  /**
   * Changes the initial azimuth of the camera
   */


  Camera.prototype.changeAzimuth = function (az) {
    this.setAzimuth(this.azimuth + az);
    return this;
  };
  /**
   * Changes the initial elevation of the camera
   */


  Camera.prototype.changeElevation = function (el) {
    this.setElevation(this.elevation + el);
    return this;
  };
  /**
   * Changes the initial roll of the camera
   */


  Camera.prototype.changeRoll = function (rl) {
    this.setRoll(this.roll + rl);
    return this;
  };
  /**
   * 设置相机方位角，不同相机模式下需要重新计算相机位置或者是视点位置
   * @param {Number} el the azimuth in degrees
   */


  Camera.prototype.setAzimuth = function (az) {
    this.azimuth = getAngle(az);
    this.computeMatrix();

    this._getAxes();

    if (this.type === CAMERA_TYPE.ORBITING || this.type === CAMERA_TYPE.EXPLORING) {
      this._getPosition();
    } else if (this.type === CAMERA_TYPE.TRACKING) {
      this._getFocalPoint();
    }

    return this;
  };

  Camera.prototype.getAzimuth = function () {
    return this.azimuth;
  };
  /**
   * 设置相机方位角，不同相机模式下需要重新计算相机位置或者是视点位置
   * @param {Number} el the elevation in degrees
   */


  Camera.prototype.setElevation = function (el) {
    this.elevation = getAngle(el);
    this.computeMatrix();

    this._getAxes();

    if (this.type === CAMERA_TYPE.ORBITING || this.type === CAMERA_TYPE.EXPLORING) {
      this._getPosition();
    } else if (this.type === CAMERA_TYPE.TRACKING) {
      this._getFocalPoint();
    }

    return this;
  };
  /**
   * 设置相机方位角，不同相机模式下需要重新计算相机位置或者是视点位置
   * @param {Number} angle the roll angle
   */


  Camera.prototype.setRoll = function (angle) {
    this.roll = getAngle(angle);
    this.computeMatrix();

    this._getAxes();

    if (this.type === CAMERA_TYPE.ORBITING || this.type === CAMERA_TYPE.EXPLORING) {
      this._getPosition();
    } else if (this.type === CAMERA_TYPE.TRACKING) {
      this._getFocalPoint();
    }

    return this;
  };
  /**
   * Changes the azimuth and elevation with respect to the current camera axes
   * @param {Number} azimuth the relative azimuth
   * @param {Number} elevation the relative elevation
   * @param {Number} roll the relative roll
   */


  Camera.prototype.rotate = function (azimuth, elevation, roll) {
    this.relElevation = getAngle(elevation);
    this.relAzimuth = getAngle(azimuth);
    this.relRoll = getAngle(roll);
    this.elevation += this.relElevation;
    this.azimuth += this.relAzimuth;
    this.roll += this.relRoll;

    if (this.type === CAMERA_TYPE.EXPLORING) {
      var rotX = quat.setAxisAngle(quat.create(), [1, 0, 0], (this.rotateWorld ? 1 : -1) * this.relElevation * DEG_2_RAD);
      var rotY = quat.setAxisAngle(quat.create(), [0, 1, 0], (this.rotateWorld ? 1 : -1) * this.relAzimuth * DEG_2_RAD);
      var rotZ = quat.setAxisAngle(quat.create(), [0, 0, 1], this.relRoll * DEG_2_RAD);
      var rotQ = quat.multiply(quat.create(), rotY, rotX);
      rotQ = quat.multiply(quat.create(), rotQ, rotZ);
      var rotMatrix = mat4.fromQuat(mat4.create(), rotQ);
      mat4.translate(this.matrix, this.matrix, [0, 0, -this.distance]);
      mat4.multiply(this.matrix, this.matrix, rotMatrix);
      mat4.translate(this.matrix, this.matrix, [0, 0, this.distance]);
    } else {
      if (Math.abs(this.elevation) > 90) {
        return;
      }

      this.computeMatrix();
    }

    this._getAxes();

    if (this.type === CAMERA_TYPE.ORBITING || this.type === CAMERA_TYPE.EXPLORING) {
      this._getPosition();
    } else if (this.type === CAMERA_TYPE.TRACKING) {
      this._getFocalPoint();
    }

    this._update();

    return this;
  };
  /**
   * 沿水平(right) & 垂直(up)平移相机
   */


  Camera.prototype.pan = function (tx, ty) {
    var coords = createVec3(tx, ty, 0);
    var pos = vec3.clone(this.position);
    vec3.add(pos, pos, vec3.scale(vec3.create(), this.right, coords[0]));
    vec3.add(pos, pos, vec3.scale(vec3.create(), this.up, coords[1]));

    this._setPosition(pos);

    this.triggerUpdate();
    return this;
  };
  /**
   * 沿 n 轴移动，当距离视点远时移动速度较快，离视点越近速度越慢
   */


  Camera.prototype.dolly = function (value) {
    var n = this.forward;
    var pos = vec3.clone(this.position);
    var step = value * this.dollyingStep;
    var updatedDistance = this.distance + value * this.dollyingStep; // 限制视点距离范围

    step = Math.max(Math.min(updatedDistance, this.maxDistance), this.minDistance) - this.distance;
    pos[0] += step * n[0];
    pos[1] += step * n[1];
    pos[2] += step * n[2];

    this._setPosition(pos);

    if (this.type === CAMERA_TYPE.ORBITING || this.type === CAMERA_TYPE.EXPLORING) {
      // 重新计算视点距离
      this._getDistance();
    } else if (this.type === CAMERA_TYPE.TRACKING) {
      // 保持视距，移动视点位置
      vec3.add(this.focalPoint, pos, this.distanceVector);
    }

    this.triggerUpdate();
    return this;
  };

  Camera.prototype.createLandmark = function (name, params) {
    var camera = this.clone();
    camera.setPosition(params.position);
    camera.setFocalPoint(params.focalPoint);

    if (params.roll !== undefined) {
      camera.setRoll(params.roll);
    }

    var landmark = new Landmark(name, camera);
    this.landmarks.push(landmark);
    return landmark;
  };

  Camera.prototype.setLandmark = function (name) {
    var landmark = new Landmark(name, this);
    this.landmarks.push(landmark);
    return this;
  };

  Camera.prototype.gotoLandmark = function (name, duration) {
    var _this = this;

    if (duration === void 0) {
      duration = 1000;
    }

    var landmark = this.landmarks.find(function (l) {
      return l.name === name;
    });

    if (landmark) {
      if (duration === 0) {
        landmark.retrieve(this);
        return;
      }

      if (this.landmarkAnimationID !== undefined) {
        cancelAnimationFrame(this.landmarkAnimationID);
      } // TODO: do not process events during animation
      // this.interactor.disconnect();


      var destPosition_1 = landmark.getPosition();
      var destFocalPoint_1 = landmark.getFocalPoint();
      var destRoll_1 = landmark.getRoll();
      var timeStart_1;

      var animate_1 = function animate_1(timestamp) {
        if (timeStart_1 === undefined) {
          timeStart_1 = timestamp;
        }

        var elapsed = timestamp - timeStart_1; // TODO: use better ease function

        var t = (1 - Math.cos(elapsed / duration * Math.PI)) / 2;
        var interFocalPoint = vec3.create();
        var interPosition = vec3.create();
        var interRoll = 0;
        vec3.lerp(interFocalPoint, _this.focalPoint, destFocalPoint_1, t);
        vec3.lerp(interPosition, _this.position, destPosition_1, t);
        interRoll = _this.roll * (1 - t) + destRoll_1 * t;

        _this.setFocalPoint(interFocalPoint);

        _this.setPosition(interPosition);

        _this.setRoll(interRoll);

        _this.computeMatrix();

        _this.triggerUpdate();

        var dist = vec3.dist(interFocalPoint, destFocalPoint_1) + vec3.dist(interPosition, destPosition_1);

        if (dist > 0.01) {//
        } else {
          _this.setFocalPoint(interFocalPoint);

          _this.setPosition(interPosition);

          _this.setRoll(interRoll);

          _this.computeMatrix(); // this.interactor.connect();


          return;
        }

        if (elapsed < duration) {
          _this.landmarkAnimationID = requestAnimationFrame(animate_1);
        }
      };

      requestAnimationFrame(animate_1);
    }
  };
  /**
   * 根据相机矩阵重新计算各种相机参数
   */


  Camera.prototype._update = function () {
    this._getAxes();

    this._getPosition();

    this._getDistance();

    this._getAngles();

    this._getOrthoMatrix();

    this.triggerUpdate();
  };
  /**
   * 计算相机矩阵
   */


  Camera.prototype.computeMatrix = function () {
    // 使用四元数描述 3D 旋转
    // @see https://xiaoiver.github.io/coding/2018/12/28/Camera-%E8%AE%BE%E8%AE%A1-%E4%B8%80.html
    var rotZ = quat.setAxisAngle(quat.create(), [0, 0, 1], this.roll * DEG_2_RAD);
    mat4.identity(this.matrix); // only consider HCS for EXPLORING and ORBITING cameras

    var rotX = quat.setAxisAngle(quat.create(), [1, 0, 0], (this.rotateWorld && this.type !== CAMERA_TYPE.TRACKING || this.type === CAMERA_TYPE.TRACKING ? 1 : -1) * this.elevation * DEG_2_RAD);
    var rotY = quat.setAxisAngle(quat.create(), [0, 1, 0], (this.rotateWorld && this.type !== CAMERA_TYPE.TRACKING || this.type === CAMERA_TYPE.TRACKING ? 1 : -1) * this.azimuth * DEG_2_RAD);
    var rotQ = quat.multiply(quat.create(), rotY, rotX);
    rotQ = quat.multiply(quat.create(), rotQ, rotZ);
    var rotMatrix = mat4.fromQuat(mat4.create(), rotQ);

    if (this.type === CAMERA_TYPE.ORBITING || this.type === CAMERA_TYPE.EXPLORING) {
      mat4.translate(this.matrix, this.matrix, this.focalPoint);
      mat4.multiply(this.matrix, this.matrix, rotMatrix);
      mat4.translate(this.matrix, this.matrix, [0, 0, this.distance]);
    } else if (this.type === CAMERA_TYPE.TRACKING) {
      mat4.translate(this.matrix, this.matrix, this.position);
      mat4.multiply(this.matrix, this.matrix, rotMatrix);
    }
  };
  /**
   * Sets the camera position in the camera matrix
   */


  Camera.prototype._setPosition = function (x, y, z) {
    this.position = createVec3(x, y, z);
    var m = this.matrix;
    m[12] = this.position[0];
    m[13] = this.position[1];
    m[14] = this.position[2];
    m[15] = 1;

    this._getOrthoMatrix();
  };
  /**
   * Recalculates axes based on the current matrix
   */


  Camera.prototype._getAxes = function () {
    vec3.copy(this.right, createVec3(vec4.transformMat4(vec4.create(), [1, 0, 0, 0], this.matrix)));
    vec3.copy(this.up, createVec3(vec4.transformMat4(vec4.create(), [0, 1, 0, 0], this.matrix)));
    vec3.copy(this.forward, createVec3(vec4.transformMat4(vec4.create(), [0, 0, 1, 0], this.matrix)));
    vec3.normalize(this.right, this.right);
    vec3.normalize(this.up, this.up);
    vec3.normalize(this.forward, this.forward);
  };
  /**
   * Recalculates euler angles based on the current state
   */


  Camera.prototype._getAngles = function () {
    // Recalculates angles
    var x = this.distanceVector[0];
    var y = this.distanceVector[1];
    var z = this.distanceVector[2];
    var r = vec3.length(this.distanceVector); // FAST FAIL: If there is no distance we cannot compute angles

    if (r === 0) {
      this.elevation = 0;
      this.azimuth = 0;
      return;
    }

    if (this.type === CAMERA_TYPE.TRACKING) {
      this.elevation = Math.asin(y / r) * RAD_2_DEG;
      this.azimuth = Math.atan2(-x, -z) * RAD_2_DEG;
    } else {
      if (this.rotateWorld) {
        this.elevation = Math.asin(y / r) * RAD_2_DEG;
        this.azimuth = Math.atan2(-x, -z) * RAD_2_DEG;
      } else {
        this.elevation = -Math.asin(y / r) * RAD_2_DEG;
        this.azimuth = -Math.atan2(-x, -z) * RAD_2_DEG;
      }
    }
  };
  /**
   * 重新计算相机位置，只有 ORBITING 模式相机位置才会发生变化
   */


  Camera.prototype._getPosition = function () {
    vec3.copy(this.position, createVec3(vec4.transformMat4(vec4.create(), [0, 0, 0, 1], this.matrix))); // 相机位置变化，需要重新计算视距

    this._getDistance();
  };
  /**
   * 重新计算视点，只有 TRACKING 模式视点才会发生变化
   */


  Camera.prototype._getFocalPoint = function () {
    vec3.transformMat3(this.distanceVector, [0, 0, -this.distance], mat3.fromMat4(mat3.create(), this.matrix));
    vec3.add(this.focalPoint, this.position, this.distanceVector); // 视点变化，需要重新计算视距

    this._getDistance();
  };
  /**
   * 重新计算视距
   */


  Camera.prototype._getDistance = function () {
    this.distanceVector = vec3.subtract(vec3.create(), this.focalPoint, this.position);
    this.distance = vec3.length(this.distanceVector);
    this.dollyingStep = this.distance / 100;
  };

  Camera.prototype._getOrthoMatrix = function () {
    if (this.projectionMode !== CAMERA_PROJECTION_MODE.ORTHOGRAPHIC) {
      return;
    }

    var position = this.position;
    var rotZ = quat.setAxisAngle(quat.create(), [0, 0, 1], -this.roll * Math.PI / 180);
    mat4.fromRotationTranslationScaleOrigin(this.orthoMatrix, rotZ, vec3.fromValues((this.rright - this.left) / 2 - position[0], (this.top - this.bottom) / 2 - position[1], 0), vec3.fromValues(this.zoom, this.zoom, 1), position);
  };

  Camera.prototype.triggerUpdate = function () {
    // update frustum
    var viewMatrix = this.getViewTransform();
    var vpMatrix = mat4.multiply(mat4.create(), this.getPerspective(), viewMatrix);
    this.getFrustum().extractFromVPMatrix(vpMatrix);
    this.emit(CameraEvent.UPDATED);
  };

  Camera.ProjectionMode = {
    ORTHOGRAPHIC: 'ORTHOGRAPHIC',
    PERSPECTIVE: 'PERSPECTIVE'
  };
  return Camera;
}(EventEmitter);

export { Camera };